import { z } from 'zod';
export const financeProjectProfitabilityCapability = 'finance.project-profitability.v1';
export const financeProjectProfitabilityProjectsSlot = 'finance.project-profitability.projects';
export const financeProjectProfitabilityProjectsProviderId = 'projects.server.finance-profitability-index';
export const financeProjectProfitabilityLaborSlot = 'finance.project-profitability.labor';
export const financeProjectProfitabilityLaborProviderId = 'timesheets.server.finance-profitability-labor';
export const financeProjectProfitabilityEquipmentSlot = 'finance.project-profitability.equipment';
export const financeProjectProfitabilityEquipmentProviderId = 'assets.server.finance-profitability-equipment';
export const financeProjectProfitabilityProcurementSlot = 'finance.project-profitability.procurement-costs';
export const financeProjectProfitabilityProcurementProviderId = 'procurement.server.finance-profitability-costs';
export const financeProjectProfitabilityContractValuesSlot = 'finance.project-profitability.contract-values';
export const financeProjectProfitabilityContractValuesProviderId = 'estimating.server.finance-profitability-contract-values';
export const financeProjectProfitabilityMoneyCentsSchema = z.number()
    .int().min(-1_000_000_000_000_000).max(1_000_000_000_000_000);
export const financeProjectProfitabilityNonnegativeCentsSchema = financeProjectProfitabilityMoneyCentsSchema.nonnegative();
export const financeProjectProfitabilityQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    projectStatus: z.string().trim().min(1).max(80).nullable().default(null),
    result: z.enum(['all', 'profitable', 'loss', 'incomplete']).default('all'),
    sort: z.enum(['profit-desc', 'revenue-desc', 'created-desc']).default('profit-desc'),
    limit: z.number().int().min(1).max(100).default(30),
    offset: z.number().int().nonnegative().default(0),
    asOf: z.string().datetime({ offset: true }).optional(),
});
export const financeProjectProfitabilityFinanceFactSchema = z.strictObject({
    projectId: z.string().uuid(),
    currencyCode: z.literal('USD'),
    invoiceCount: z.number().int().nonnegative(),
    invoicedRevenueCents: financeProjectProfitabilityNonnegativeCentsSchema,
    collectedRevenueCents: financeProjectProfitabilityNonnegativeCentsSchema,
    writtenOffCents: financeProjectProfitabilityNonnegativeCentsSchema,
    outstandingCents: financeProjectProfitabilityNonnegativeCentsSchema,
    approvedDirectExpenseCount: z.number().int().nonnegative(),
    approvedDirectExpenseCents: financeProjectProfitabilityNonnegativeCentsSchema,
    referralCommissionCount: z.number().int().nonnegative(),
    referralCommissionCents: financeProjectProfitabilityNonnegativeCentsSchema,
    overhead: z.strictObject({
        source: z.enum(['locked-allocation', 'dynamic-policy', 'disabled']),
        allocationCount: z.number().int().nonnegative(),
        allocationRatePartsPerMillion: z.number().int().min(0).max(10_000_000).nullable(),
        overheadCents: financeProjectProfitabilityNonnegativeCentsSchema,
    }),
}).superRefine((fact, context) => {
    if (fact.collectedRevenueCents + fact.writtenOffCents + fact.outstandingCents !==
        fact.invoicedRevenueCents) {
        context.addIssue({
            code: 'custom',
            path: ['invoicedRevenueCents'],
            message: 'Finance profitability revenue must reconcile exactly.',
        });
    }
    if (fact.overhead.source === 'locked-allocation' && fact.overhead.allocationCount === 0) {
        context.addIssue({
            code: 'custom', path: ['overhead', 'allocationCount'],
            message: 'Locked overhead needs at least one retained allocation.',
        });
    }
});
export const financeProjectProfitabilityProjectsResponseSchema = z.strictObject({
    contractVersion: z.literal('projects.finance-profitability-index.v1'),
    tenantId: z.string().uuid(),
    totalProjects: z.number().int().nonnegative(),
    truncated: z.boolean(),
    projects: z.array(z.strictObject({
        id: z.string().uuid(),
        projectNumber: z.string().trim().min(1).max(80).nullable(),
        name: z.string().trim().min(1).max(240),
        status: z.string().trim().min(1).max(80),
        revision: z.number().int().positive(),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
    })).max(500),
}).superRefine((response, context) => {
    if (response.projects.length > response.totalProjects) {
        context.addIssue({
            code: 'custom', path: ['totalProjects'],
            message: 'The Projects profitability total cannot be below the returned count.',
        });
    }
    if (response.truncated !== (response.projects.length < response.totalProjects)) {
        context.addIssue({
            code: 'custom', path: ['truncated'],
            message: 'The Projects profitability truncation flag is inconsistent.',
        });
    }
    const ids = response.projects.map(({ id }) => id);
    if (new Set(ids).size !== ids.length) {
        context.addIssue({ code: 'custom', path: ['projects'], message: 'Projects cannot repeat.' });
    }
});
export const financeProjectProfitabilityLaborProjectSchema = z.discriminatedUnion('state', [
    z.strictObject({
        state: z.literal('present'),
        projectId: z.string().uuid(),
        currencyCode: z.string().regex(/^[A-Z]{3}$/),
        trackedWorkSeconds: z.number().int().nonnegative(),
        payableEntryCount: z.number().int().nonnegative(),
        pendingEntryCount: z.number().int().nonnegative(),
        payableLaborCents: financeProjectProfitabilityNonnegativeCentsSchema,
        pendingLaborCents: financeProjectProfitabilityNonnegativeCentsSchema,
        totalLaborCostCents: financeProjectProfitabilityNonnegativeCentsSchema,
        policyVersionIds: z.array(z.string().uuid()).max(10_000),
    }),
    z.strictObject({
        state: z.literal('unavailable'),
        projectId: z.string().uuid(),
        reasonCode: z.enum([
            'history-truncated',
            'basis-incomplete',
            'currency-mismatch',
            'permission-denied',
        ]),
    }),
]);
export const financeProjectProfitabilityLaborResponseSchema = z.strictObject({
    contractVersion: z.literal('timesheets.finance-profitability-labor.v1'),
    tenantId: z.string().uuid(),
    fromInclusive: z.string().datetime({ offset: true }),
    toExclusive: z.string().datetime({ offset: true }),
    projects: z.array(financeProjectProfitabilityLaborProjectSchema).min(1).max(500),
}).superRefine((response, context) => {
    const ids = response.projects.map(({ projectId }) => projectId);
    if (new Set(ids).size !== ids.length) {
        context.addIssue({ code: 'custom', path: ['projects'], message: 'Labor facts cannot repeat.' });
    }
    response.projects.forEach((project, index) => {
        if (project.state === 'present' &&
            project.totalLaborCostCents !== project.payableLaborCents + project.pendingLaborCents) {
            context.addIssue({
                code: 'custom', path: ['projects', index, 'totalLaborCostCents'],
                message: 'Labor costs do not reconcile.',
            });
        }
    });
});
export const financeProjectProfitabilityEquipmentProjectSchema = z.discriminatedUnion('state', [
    z.strictObject({
        state: z.literal('present'),
        projectId: z.string().uuid(),
        asOf: z.string().datetime({ offset: true }),
        currencyCode: z.string().regex(/^[A-Z]{3}$/).nullable(),
        activePlacementCount: z.number().int().nonnegative(),
        totalPlacementCount: z.number().int().nonnegative(),
        totalBillablePeriods: z.number().int().nonnegative(),
        equipmentCostCents: financeProjectProfitabilityNonnegativeCentsSchema,
    }),
    z.strictObject({
        state: z.literal('unavailable'),
        projectId: z.string().uuid(),
        reasonCode: z.enum(['permission-denied', 'project-unavailable', 'cost-basis-incomplete']),
    }),
]);
export const financeProjectProfitabilityEquipmentResponseSchema = z.strictObject({
    contractVersion: z.literal('assets.finance-profitability-equipment.v1'),
    tenantId: z.string().uuid(),
    asOf: z.string().datetime({ offset: true }),
    projects: z.array(financeProjectProfitabilityEquipmentProjectSchema).min(1).max(500),
}).superRefine((response, context) => {
    response.projects.forEach((project, index) => {
        if (project.state !== 'present')
            return;
        if (project.totalPlacementCount === 0 && (project.activePlacementCount !== 0 || project.totalBillablePeriods !== 0 ||
            project.equipmentCostCents !== 0 || project.currencyCode !== null)) {
            context.addIssue({
                code: 'custom', path: ['projects', index],
                message: 'Empty equipment facts must be exact zero.',
            });
        }
        if (project.totalPlacementCount > 0 && project.currencyCode === null) {
            context.addIssue({
                code: 'custom', path: ['projects', index, 'currencyCode'],
                message: 'Priced equipment needs currency.',
            });
        }
    });
});
export const financeProjectProfitabilityProcurementProjectSchema = z.discriminatedUnion('state', [
    z.strictObject({
        state: z.literal('present'),
        projectId: z.string().uuid(),
        currencyCode: z.string().regex(/^[A-Z]{3}$/).nullable(),
        orderCount: z.number().int().nonnegative(),
        openOrderCount: z.number().int().nonnegative(),
        materialOrderCount: z.number().int().nonnegative(),
        subcontractOrderCount: z.number().int().nonnegative(),
        materialCostCents: financeProjectProfitabilityNonnegativeCentsSchema,
        subcontractorCostCents: financeProjectProfitabilityNonnegativeCentsSchema,
        totalProcurementCostCents: financeProjectProfitabilityNonnegativeCentsSchema,
    }),
    z.strictObject({
        state: z.literal('unavailable'),
        projectId: z.string().uuid(),
        reasonCode: z.enum(['permission-denied', 'project-unavailable', 'cost-basis-incomplete']),
    }),
]);
export const financeProjectProfitabilityProcurementResponseSchema = z.strictObject({
    contractVersion: z.literal('procurement.finance-profitability-costs.v1'),
    tenantId: z.string().uuid(),
    projects: z.array(financeProjectProfitabilityProcurementProjectSchema).min(1).max(500),
}).superRefine((response, context) => {
    response.projects.forEach((project, index) => {
        if (project.state !== 'present')
            return;
        if (project.totalProcurementCostCents !==
            project.materialCostCents + project.subcontractorCostCents) {
            context.addIssue({
                code: 'custom', path: ['projects', index, 'totalProcurementCostCents'],
                message: 'Procurement costs do not reconcile.',
            });
        }
        if (project.orderCount === 0 && (project.currencyCode !== null || project.openOrderCount !== 0 ||
            project.materialOrderCount !== 0 || project.subcontractOrderCount !== 0 ||
            project.totalProcurementCostCents !== 0)) {
            context.addIssue({
                code: 'custom', path: ['projects', index],
                message: 'Empty Procurement facts must be exact zero.',
            });
        }
    });
});
export const financeProjectProfitabilityContractValueProjectSchema = z.discriminatedUnion('state', [
    z.strictObject({
        state: z.literal('present'),
        projectId: z.string().uuid(),
        estimateId: z.string().uuid(),
        estimateVersionId: z.string().uuid(),
        estimateRevision: z.number().int().positive(),
        estimateContentHash: z.string().regex(/^[a-f0-9]{64}$/),
        acceptedAt: z.string().datetime({ offset: true }),
        currencyCode: z.string().regex(/^[A-Z]{3}$/),
        contractValueCents: financeProjectProfitabilityNonnegativeCentsSchema,
    }),
    z.strictObject({
        state: z.literal('missing'),
        projectId: z.string().uuid(),
        reasonCode: z.literal('no-accepted-estimate'),
    }),
    z.strictObject({
        state: z.literal('unavailable'),
        projectId: z.string().uuid(),
        reasonCode: z.literal('source-limit-exceeded'),
    }),
]);
export const financeProjectProfitabilityContractValuesResponseSchema = z.strictObject({
    contractVersion: z.literal('estimating.finance-profitability-contract-values.v1'),
    tenantId: z.string().uuid(),
    acceptedEstimateCount: z.number().int().nonnegative(),
    truncated: z.boolean(),
    projects: z.array(financeProjectProfitabilityContractValueProjectSchema).min(1).max(500),
}).superRefine((response, context) => {
    const ids = response.projects.map(({ projectId }) => projectId);
    if (new Set(ids).size !== ids.length) {
        context.addIssue({
            code: 'custom', path: ['projects'], message: 'Contract values cannot repeat a Project.',
        });
    }
    if (response.truncated !== response.projects.some(({ state }) => state === 'unavailable')) {
        context.addIssue({
            code: 'custom', path: ['truncated'],
            message: 'Contract-value truncation must match unavailable source-limit facts.',
        });
    }
});
export const financeProjectProfitabilityUnavailableReasonSchema = z.enum([
    'not-installed', 'not-enabled', 'permission-denied', 'provider-invalid', 'provider-failed',
    'history-truncated', 'basis-incomplete', 'currency-mismatch', 'project-unavailable',
    'cost-basis-incomplete', 'source-limit-exceeded', 'not-implemented',
]);
export const financeProjectProfitabilitySourceStateSchema = z.discriminatedUnion('state', [
    z.strictObject({
        state: z.literal('present'),
        capability: z.string().trim().min(1).max(180),
        providerModuleId: z.string().trim().min(1).max(80),
    }),
    z.strictObject({
        state: z.literal('missing'),
        capability: z.string().trim().min(1).max(180),
        providerModuleId: z.string().trim().min(1).max(80),
        reasonCode: z.literal('no-accepted-estimate'),
    }),
    z.strictObject({
        state: z.literal('unavailable'),
        capability: z.string().trim().min(1).max(180),
        providerModuleId: z.string().trim().min(1).max(80),
        reasonCode: financeProjectProfitabilityUnavailableReasonSchema,
    }),
]);
export const financeProjectProfitabilityContractValueSchema = z.discriminatedUnion('state', [
    financeProjectProfitabilityContractValueProjectSchema.options[0],
    financeProjectProfitabilityContractValueProjectSchema.options[1],
    z.strictObject({
        state: z.literal('unavailable'),
        projectId: z.string().uuid(),
        reasonCode: financeProjectProfitabilityUnavailableReasonSchema,
    }),
]);
export const financeProjectProfitabilityProjectSchema = z.strictObject({
    contractVersion: z.literal(financeProjectProfitabilityCapability),
    projectId: z.string().uuid(),
    projectNumber: z.string().trim().min(1).max(80).nullable(),
    projectName: z.string().trim().min(1).max(240),
    projectStatus: z.string().trim().min(1).max(80),
    projectRevision: z.number().int().positive(),
    projectCreatedAt: z.string().datetime({ offset: true }),
    projectUpdatedAt: z.string().datetime({ offset: true }),
    currencyCode: z.literal('USD'),
    contractValue: financeProjectProfitabilityContractValueSchema,
    revenue: z.strictObject({
        invoiceCount: z.number().int().nonnegative(),
        invoicedCents: financeProjectProfitabilityNonnegativeCentsSchema,
        collectedCents: financeProjectProfitabilityNonnegativeCentsSchema,
        writtenOffCents: financeProjectProfitabilityNonnegativeCentsSchema,
        outstandingCents: financeProjectProfitabilityNonnegativeCentsSchema,
    }),
    costs: z.strictObject({
        trackedLaborSeconds: z.number().int().nonnegative().nullable(),
        payableLaborCents: financeProjectProfitabilityNonnegativeCentsSchema.nullable(),
        pendingLaborCents: financeProjectProfitabilityNonnegativeCentsSchema.nullable(),
        laborCents: financeProjectProfitabilityNonnegativeCentsSchema.nullable(),
        equipmentCents: financeProjectProfitabilityNonnegativeCentsSchema.nullable(),
        materialCents: financeProjectProfitabilityNonnegativeCentsSchema.nullable(),
        subcontractorCents: financeProjectProfitabilityNonnegativeCentsSchema.nullable(),
        directExpenseCents: financeProjectProfitabilityNonnegativeCentsSchema,
        referralCommissionCents: financeProjectProfitabilityNonnegativeCentsSchema.nullable(),
        overheadCents: financeProjectProfitabilityNonnegativeCentsSchema,
        knownCostCents: financeProjectProfitabilityNonnegativeCentsSchema,
        totalCostWithoutOverheadCents: financeProjectProfitabilityNonnegativeCentsSchema.nullable(),
        totalCostCents: financeProjectProfitabilityNonnegativeCentsSchema.nullable(),
    }),
    profitability: z.strictObject({
        complete: z.boolean(),
        incompleteReasonCodes: z.array(financeProjectProfitabilityUnavailableReasonSchema),
        knownContributionCents: financeProjectProfitabilityMoneyCentsSchema,
        profitWithoutOverheadCents: financeProjectProfitabilityMoneyCentsSchema.nullable(),
        profitCents: financeProjectProfitabilityMoneyCentsSchema.nullable(),
        marginWithoutOverheadBasisPoints: z.number().int().min(-1_000_000).max(1_000_000).nullable(),
        marginBasisPoints: z.number().int().min(-1_000_000).max(1_000_000).nullable(),
    }),
    overhead: financeProjectProfitabilityFinanceFactSchema.shape.overhead,
    sources: z.strictObject({
        labor: financeProjectProfitabilitySourceStateSchema,
        equipment: financeProjectProfitabilitySourceStateSchema,
        procurement: financeProjectProfitabilitySourceStateSchema,
        contractValue: financeProjectProfitabilitySourceStateSchema,
        referralCommission: financeProjectProfitabilitySourceStateSchema,
    }),
});
export const financeProjectProfitabilityResponseSchema = z.strictObject({
    contractVersion: z.literal(financeProjectProfitabilityCapability),
    tenantId: z.string().uuid(),
    asOf: z.string().datetime({ offset: true }),
    historyFromInclusive: z.string().datetime({ offset: true }),
    sourceProjectCount: z.number().int().nonnegative(),
    sourceProjectsTruncated: z.boolean(),
    availableProjectStatuses: z.array(z.string().trim().min(1).max(80)).max(500),
    data: z.array(financeProjectProfitabilityProjectSchema).max(100),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        hasMore: z.boolean(),
    }),
    totals: z.strictObject({
        projectCount: z.number().int().nonnegative(),
        completeProjectCount: z.number().int().nonnegative(),
        incompleteProjectCount: z.number().int().nonnegative(),
        invoicedCents: financeProjectProfitabilityNonnegativeCentsSchema,
        collectedCents: financeProjectProfitabilityNonnegativeCentsSchema,
        knownCostCents: financeProjectProfitabilityNonnegativeCentsSchema,
        knownContributionCents: financeProjectProfitabilityMoneyCentsSchema,
        totalCostCents: financeProjectProfitabilityNonnegativeCentsSchema.nullable(),
        profitCents: financeProjectProfitabilityMoneyCentsSchema.nullable(),
        marginBasisPoints: z.number().int().min(-1_000_000).max(1_000_000).nullable(),
    }),
}).superRefine((response, context) => {
    if (new Set(response.availableProjectStatuses).size !== response.availableProjectStatuses.length) {
        context.addIssue({
            code: 'custom', path: ['availableProjectStatuses'],
            message: 'Profitability Project status options cannot repeat.',
        });
    }
});
//# sourceMappingURL=finance-project-profitability.contract.js.map